import './Header.css';
import { ReactComponent as PieChartIcon } from '../../../../assets/icons/pie-chart-line.svg';
import { ReactComponent as EarthIcon } from '../../../../assets/icons/earth-line.svg';
import {
  FundInfoData,
  FundQuoteData,
} from '../../../../types/fundsummary.type';
import { formatDetailDate } from '../../../../helpers/date';
import { formatLocaleNumber } from '../../../../utils/utils';
import { addPrefixNumber, formatPercent } from '../../../../helpers/number';

export interface FundSummaryHeaderProps {
  info?: FundInfoData;
  quote?: FundQuoteData;
}

const PRICE_LABEL = 'NAV';
const EMPTY = '-';

const parseNum = (value?: string): number | null => {
  if (value == null || value.trim() === '' || value.trim() === EMPTY) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const FundSummaryHeader = ({
  info,
  quote,
}: FundSummaryHeaderProps): JSX.Element => {
  const currency = info?.fundDetailInfo?.classCcy ?? '';
  const name = info?.fundDetailInfo?.fullName;
  const bellCode = info?.fundDetailInfo?.bellCode;
  const price = quote?.TRDPRC_1;
  const netChange = parseNum(quote?.NETCHNG_1);
  const changePercent = parseNum(quote?.PCTCHNG);
  const geographicalFocus = info?.fundDetailInfo?.enGeographicalFocus;
  const date = quote?.TRADE_DATE
    ? formatDetailDate(quote.TRADE_DATE, false, 'DMY')
    : undefined;
  const assetType = info?.fundDetailInfo?.enAssetType ?? '';

  const direction =
    netChange == null || netChange === 0
      ? 'unchg'
      : netChange > 0
        ? 'up'
        : 'down';

  return (
    <div className="fund-summary-header">
      <div className="fund-summary-header__top">
        <div className="fund-summary-header__name">
          <span className="fund-summary-header__ticker">{bellCode}</span>
          {name && (
            <span className="fund-summary-header__instrument" title={name}>
              {name}
            </span>
          )}
        </div>

        <div className="fund-summary-header__desc">
          {assetType && (
            <div className="fund-summary-header__desc-item">
              <PieChartIcon className="fund-summary-header__icon" />
              <span className="fund-summary-header__desc-text">
                {assetType}
              </span>
            </div>
          )}
          {geographicalFocus && (
            <div className="fund-summary-header__desc-item">
              <EarthIcon className="fund-summary-header__icon" />
              <span className="fund-summary-header__desc-text">
                {geographicalFocus}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="fund-summary-header__prices">
        <div className="fund-summary-header__price-row">
          <p className="fund-summary-header__price">
            <span className="fund-summary-header__price-label">
              {PRICE_LABEL}
            </span>
            <span className="fund-summary-header__price-value">
              {formatLocaleNumber(price, 2)}
            </span>
            <span className="fund-summary-header__price-currency">
              {currency}
            </span>
          </p>

          <p
            className={`fund-summary-header__change fund-summary-header__change--${direction}`}
          >
            {addPrefixNumber(formatLocaleNumber(netChange, 2))}
          </p>
          <p
            className={`fund-summary-header__change fund-summary-header__change--${direction}`}
          >
            {changePercent != null && <>{formatPercent(changePercent, 2)}</>}
          </p>

          {date && <p className="fund-summary-header__date">{date}</p>}
        </div>
      </div>
    </div>
  );
};

export default FundSummaryHeader;
